import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pracaService, Praca, PracaRequest } from '../../services/praca';
import { funcionarioService, Funcionario } from '../../services/funcionario';
import DatePickerField from '../common/DatePickerField';

interface PracaFormProps {
  isEdit?: boolean;
}

const PracaForm: React.FC<PracaFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = React.useState({
    nomePraca: '',
    codFuncionario: '',
    dataVinculo: '',
  });
  
  const [errors, setErrors] = React.useState({
    nomePraca: '',
    codFuncionario: '',
    dataVinculo: '',
  });

  // Buscar praça para edição
  const { data: praca, isLoading } = useQuery({
    queryKey: ['praca', id],
    queryFn: () => pracaService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  // Buscar funcionários para o select
  const { data: funcionarios = [] } = useQuery({
    queryKey: ['funcionarios'],
    queryFn: funcionarioService.getAll,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: PracaRequest) => pracaService.save(data),
    onSuccess: () => {
      // Invalidar o cache para forçar atualização
      queryClient.invalidateQueries({ queryKey: ['pracas'] });
      navigate('/pracas');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar praça:', error);
    },
  });

  useEffect(() => {
    if (praca && isEdit) {
      // Converte data de yyyy-mm-dd para dd/mm/yyyy para exibição
      let dataFormatada = '';
      if (praca.dataVinculo) {
        try {
          const data = new Date(praca.dataVinculo);
          if (!isNaN(data.getTime())) {
            const dia = String(data.getDate()).padStart(2, '0');
            const mes = String(data.getMonth() + 1).padStart(2, '0');
            const ano = data.getFullYear();
            dataFormatada = `${dia}/${mes}/${ano}`;
          }
        } catch (error) {
          console.error('Erro ao converter data:', error);
        }
      }

      setFormData({
        nomePraca: praca.nomePraca,
        codFuncionario: praca.codFuncionario.toString(),
        dataVinculo: dataFormatada,
      });
    }
  }, [praca, isEdit]);

  const validateForm = (): boolean => {
    const newErrors = {
      nomePraca: '',
      codFuncionario: '',
      dataVinculo: '',
    };
    let isValid = true;

    // Validação Nome da Praça
    if (!formData.nomePraca.trim()) {
      newErrors.nomePraca = 'Nome da praça é obrigatório';
      isValid = false;
    } else if (formData.nomePraca.length > 50) {
      newErrors.nomePraca = 'Nome não deve possuir mais que 50 caracteres';
      isValid = false;
    }

    // Validação Funcionário
    if (!formData.codFuncionario) {
      newErrors.codFuncionario = 'Funcionário é obrigatório';
      isValid = false;
    }

    // Validação Data de Vínculo
    if (!formData.dataVinculo) {
      newErrors.dataVinculo = 'Data de vínculo é obrigatória';
      isValid = false;
    } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(formData.dataVinculo)) {
      newErrors.dataVinculo = 'Data deve estar no formato DD/MM/AAAA';
      isValid = false;
    } else {
      // Verifica se a data é válida
      const [dia, mes, ano] = formData.dataVinculo.split('/').map(Number);
      const data = new Date(ano, mes - 1, dia);
      if (isNaN(data.getTime()) || data.getDate() !== dia || data.getMonth() !== mes - 1 || data.getFullYear() !== ano) {
        newErrors.dataVinculo = 'Data inválida';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Converte data de dd/mm/yyyy para yyyy-mm-dd para o backend
      let dataFormatada = '';
      if (formData.dataVinculo && /^\d{2}\/\d{2}\/\d{4}$/.test(formData.dataVinculo)) {
        const [dia, mes, ano] = formData.dataVinculo.split('/');
        dataFormatada = `${ano}-${mes}-${dia}`;
      }

      const pracaData: PracaRequest = {
        id: isEdit ? Number(id) : undefined,
        nomePraca: formData.nomePraca.trim(),
        codFuncionario: Number(formData.codFuncionario),
        dataVinculo: dataFormatada,
      };
      
      saveMutation.mutate(pracaData);
    }
  };

  const handleClear = () => {
    setFormData({
      nomePraca: '',
      codFuncionario: '',
      dataVinculo: '',
    });
    setErrors({
      nomePraca: '',
      codFuncionario: '',
      dataVinculo: '',
    });
  };

  const handleBack = () => {
    navigate('/pracas');
  };

  const handleInputChange = (field: keyof typeof formData) => (e: any) => {
    const value = typeof e === 'string' ? e : (e.target as HTMLInputElement).value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  if (isEdit && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {isEdit ? 'Editar Praça' : 'Nova Praça'}
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Praça"
            value={formData.nomePraca}
            onChange={handleInputChange('nomePraca')}
            error={!!errors.nomePraca}
            helperText={errors.nomePraca}
            slotProps={{
              htmlInput: { maxLength: 50 }
            }}
            required
          />
          
          <FormControl fullWidth>
            <InputLabel id="funcionario-label">Funcionário</InputLabel>
            <Select
              labelId="funcionario-label"
              value={formData.codFuncionario}
              onChange={handleInputChange('codFuncionario')}
              error={!!errors.codFuncionario}
              required
            >
              {funcionarios.map((funcionario) => (
                <MenuItem key={funcionario.id} value={funcionario.id.toString()}>
                  {funcionario.nome}
                </MenuItem>
              ))}
            </Select>
            {errors.codFuncionario && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                {errors.codFuncionario}
              </Typography>
            )}
          </FormControl>
          
          <DatePickerField
            label="Data Vínculo Praça-Funcionário"
            value={formData.dataVinculo}
            onChange={handleInputChange('dataVinculo')}
            error={!!errors.dataVinculo}
            helperText={errors.dataVinculo}
            required
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PracaForm;
